import { Component, OnInit, Input, Output, EventEmitter, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ICustomerAccountVisit } from '@app/customer-account/models/customer-account-visit.model';
import { IVisitCancellationRequestResult } from '@app/customer-account/models/visit-cancellation-request-result.model';
import { VisitCancellationStatusEnum } from '@app/customer-account/models/visit-cancellation-status.enum';
import { VisitStatusEnum } from '@app/customer-account/models/visit-status.enum';
import { CustomerAccountVisitsService } from '@app/customer-account/services/customer-account-visits.service';
import { DialogsService } from '@app/shared/services/dialogs/dialogs.service';
import { OkDialogResult } from '@app/shared/services/dialogs/ok-dialog/ok-dialog-result.enum';
import { YesNoDialogResult } from '@app/shared/services/dialogs/yes-no-dialog/yes-no-dialog-result.enum';
import { inOutAnimation } from '@app/shared/utils';
import { differenceInYears } from 'date-fns';
import { Observable, filter, switchMap } from 'rxjs';

@Component({
  selector: 'app-customer-account-visit',
  templateUrl: './customer-account-visit.component.html',
  styleUrls: ['./customer-account-visit.component.scss'],
  animations: [inOutAnimation]
})
export class CustomerAccountVisitComponent implements OnInit {
  @Input() visit: ICustomerAccountVisit;
  @Output() visitCancelled?= new EventEmitter<string>();

  public VisitStatusEnum = VisitStatusEnum;
  public visitIsNotInThisYear: boolean;
  public visitStatusTranslationKey: string;
  public formattedSalonAddress: string;
  private readonly router = inject(Router);
  private readonly dialogsService = inject(DialogsService);
  private readonly customerAccountVisitsService = inject(CustomerAccountVisitsService);

  ngOnInit(): void {
    this.setupProps();
  }

  toggleShowMore(): void {
    this.visit.showMore = !this.visit.showMore;
  }

  goToSalonPage(): void {
    this.router.navigate(['salon', this.visit.salonCity, this.visit.salonNameAlias]);
  }

  tryCancelVisit(): void {
    this.askIfUserIsSure()
      .pipe(
        filter((dialogResult: YesNoDialogResult) => dialogResult === YesNoDialogResult.Yes),
        switchMap(() => this.customerAccountVisitsService.sendVisitCancellationRequest(this.visit.visitId, this.visit.salonId)),
        switchMap((result: IVisitCancellationRequestResult) => {
          if (result.cancelVisitStatus === VisitCancellationStatusEnum.Success) {
            this.visitCancelled.next(result.visitId);
            return this.showSuccessMessage(result.salonPhoneNumber);
          }
          return this.showFailureMessage(result.salonPhoneNumber);
        })
      )
      .subscribe();
  }

  private setupProps(): void {
    this.visitIsNotInThisYear = this.isNotInThisYear(this.visit.visitDate);
    this.visitStatusTranslationKey = this.getVisitStatusTranslationKey(this.visit.visitStatus);
  }

  private isNotInThisYear(visitDate: Date): boolean {
    const givenDate = new Date(visitDate);
    const nowDate = new Date();
    return differenceInYears(nowDate, givenDate) > 0;
  }

  private getVisitStatusTranslationKey(visitStatus: VisitStatusEnum): string {
    return `CUSTOMER-ACCOUNT.VISITS-VIEW.VISIT-STATUS.${VisitStatusEnum[visitStatus]}`.toUpperCase();
  }

  private askIfUserIsSure(): Observable<YesNoDialogResult> {
    return this.dialogsService.showYesNoDialog(
      'CUSTOMER-ACCOUNT.VISITS-VIEW.CONFIRM-VISIT-CANCEL-MODAL.QUESTION',
      {
        yesButtonTextTranslationKey: 'CUSTOMER-ACCOUNT.VISITS-VIEW.CONFIRM-VISIT-CANCEL-MODAL.YES-BUTTON-TEXT',
        noButtonTextTranslationKey: 'CUSTOMER-ACCOUNT.VISITS-VIEW.CONFIRM-VISIT-CANCEL-MODAL.NO-BUTTON-TEXT',
        cautionMode: true,
        invertButtonsOrder: true
      });
  }

  private showSuccessMessage(salonPhoneNumber: string): Observable<OkDialogResult> {
    return this.dialogsService.showOkDialog(
      'CUSTOMER-ACCOUNT.VISITS-VIEW.CANCELLATION-SUCCESS-MODAL.TEXT',
      {
        dissmissable: false,
        acceptHTML: true,
        showIcon: true,
        iconClass: 'check-circle',
        okButtonTextTranslationKey: 'CUSTOMER-ACCOUNT.VISITS-VIEW.CANCELLATION-SUCCESS-MODAL.BUTTON-CLOSE'
      },
      {
        salonPhoneNumber,
        salonPageUrl: `salon\\${this.visit.salonCity}\\${this.visit.salonNameAlias}`
      }
    );
  }

  private showFailureMessage(salonPhoneNumber: string): Observable<OkDialogResult> {
    return this.dialogsService.showOkDialog(
      'CUSTOMER-ACCOUNT.VISITS-VIEW.CANCELLATION-FAILURE-MODAL.TEXT',
      {
        dissmissable: false,
        acceptHTML: true,
        showIcon: true,
        iconClass: 'alert-octagon',
        okButtonTextTranslationKey: 'CUSTOMER-ACCOUNT.VISITS-VIEW.CANCELLATION-FAILURE-MODAL.BUTTON-CLOSE'
      },
      {
        salonPhoneNumber
      }
    );
  }
}
